/**
 * Resolves which `webinars` row backs a public webinar URL.
 *
 * Two storage shapes are in use:
 *  - one row per language (`lang` = 'en' | 'es' | 'zh-cn');
 *  - a single row carrying localized columns (`slug_es`/`content_es`, `slug_zh`/`content_zh`).
 *
 * A Chinese URL therefore has to accept a row whose `lang` is 'en' as long as that
 * row publishes the URL through `slug_zh` — filtering the `slug_zh` fallback on
 * `lang` leaves those pages showing "webinar not found" once the client re-fetches,
 * even though the server-rendered HTML is correct. The Spanish `slug_es` fallback
 * has always followed this same rule.
 */

export interface WebinarRouteOpts {
  isSpanish: boolean;
  isChinese: boolean;
  isPreview: boolean;
}

/** Drafts (no published_at) are only reachable with ?preview=1 */
const onlyPublished = (query: any, isPreview: boolean) =>
  isPreview ? query : query.not("published_at", "is", null);

export async function fetchWebinarForRoute(
  client: any,
  slug: string,
  { isSpanish, isChinese, isPreview }: WebinarRouteOpts,
): Promise<Record<string, any> | null> {
  // Try exact slug match first
  let query: any = onlyPublished(client.from("webinars").select("*").eq("slug", slug), isPreview);

  // Chinese detail routes must resolve a Chinese row rather than an English
  // row whose canonical slug happens to match the public Chinese slug.
  if (isChinese) {
    query = query.eq("lang", "zh-cn");
  } else if (!isSpanish) {
    query = query.neq("lang", "es");
  }

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  if (data) return data;

  // Fallback: try slug_es for Spanish paths
  if (isSpanish) {
    const { data: esData, error: esErr } = await onlyPublished(
      client.from("webinars").select("*").eq("slug_es", slug),
      isPreview,
    ).maybeSingle();
    if (esErr) throw esErr;
    return esData;
  }

  // Fallback: try slug_zh for Chinese paths. A row that publishes this public slug
  // through `slug_zh` IS the Chinese version, even when it lives in a single
  // localized row (`lang` = 'en'), so no `lang` filter here.
  if (isChinese) {
    const { data: zhData, error: zhErr } = await onlyPublished(
      client.from("webinars").select("*").eq("slug_zh", slug),
      isPreview,
    ).maybeSingle();
    if (zhErr) throw zhErr;
    return zhData;
  }

  return null;
}
